/**
 * NamespaceModal
 *
 * Modal to switch between user namespaces with lazy fetch and retry states.
 * Provides quick links to create and manage namespaces and closes on outside
 * click or Escape.
 */
import React, { useRef, useEffect, useState } from 'react';
import { X, Plus, Building, Settings, Loader2, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNamespace } from '../../../context/namespace';
import { useSidebar } from '../../../context/sidebar';

export default function NamespaceModal() {
  const navigate = useNavigate();
  const { 
    isNamespaceModalOpen, 
    closeNamespaceModal, 
    namespaces, 
    currentNamespace,
    switchNamespace,
    isLoading,
    error,
    fetchUserNamespaces
  } = useNamespace();
  const { closeMobileSidebar } = useSidebar();
  const [isRetrying, setIsRetrying] = useState(false);

  const modalRef = useRef(null);

  // Fetch namespaces when modal is opened
  useEffect(() => {
    if (isNamespaceModalOpen) {
      // Only fetch if we don't already have namespaces to avoid unnecessary API calls
      if (namespaces.length === 0 && !isLoading && !error) {
        console.log('Modal opened - fetching namespaces');
        fetchUserNamespaces(false);
      } else {
        console.log('Modal opened - using existing namespaces data');
      }
    }
  }, [isNamespaceModalOpen, fetchUserNamespaces, namespaces.length, isLoading, error]);

  // Close modal when clicking outside
  useEffect(() => {
    if (!isNamespaceModalOpen) return;
    
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeNamespaceModal();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNamespaceModalOpen, closeNamespaceModal]);

  // Close modal when pressing Escape key
  useEffect(() => {
    if (!isNamespaceModalOpen) return;
    
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        closeNamespaceModal();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isNamespaceModalOpen, closeNamespaceModal]);

  if (!isNamespaceModalOpen) return null;

  const handleNavigateToCreate = () => {
    closeNamespaceModal();
    closeMobileSidebar();
    navigate('/app/namespaces/create');
  };

  const handleNavigateToManage = () => {
    closeNamespaceModal();
    closeMobileSidebar();
    navigate('/app/namespaces/manage');
  };

  const handleSelectNamespace = (namespace) => {
    switchNamespace(namespace.name);
    closeMobileSidebar();
  };

  const handleRetry = () => {
    setIsRetrying(true);
    fetchUserNamespaces(true)
      .then(() => {
        console.log('Retry successful');
      })
      .catch((err) => {
        console.error('Retry failed:', err);
      })
      .finally(() => {
        setIsRetrying(false);
      });
  };

  // Function to display namespace name safely
  const getNamespaceName = (namespace) => {
    if (typeof namespace === 'string') return namespace;
    return namespace?.name || 'Unknown';
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div 
        ref={modalRef}
        className="relative w-full max-w-xl rounded-2xl bg-white p-8 shadow-xl dark:bg-gray-900 mx-4 animate-in fade-in zoom-in duration-200"
      >
        <button
          onClick={closeNamespaceModal}
          className="absolute right-6 top-6 rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          <X className="h-5 w-5" />
        </button>
        
        <div className="mb-8">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50">
              <Building className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Namespaces
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Switch between your available namespaces
              </p>
            </div>
          </div>
          
          <div className="mt-4 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800/30">
            <h4 className="font-medium text-purple-700 dark:text-purple-300 mb-1 text-sm">What are Namespaces?</h4>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              Namespaces help you organize your work in Advocacy Lab. Think of them as separate workspaces where you can:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Group templates and campaigns by categories, companies, or organizations</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Switch easily between different projects or clients</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Keep your work organized and separate for better management</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="space-y-6">
          {/* Available Namespaces */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Your Namespaces</h4>
            </div>
            
            {/* Loading State */}
            {(isLoading || isRetrying) && (
              <div className="flex flex-col items-center justify-center py-5 px-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-purple-600 dark:text-purple-400" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {isRetrying ? 'Retrying...' : 'Loading your namespaces...'}
                  </p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && !isLoading && !isRetrying && (
              <div className="p-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-center">
                <div className="flex flex-col items-center justify-center">
                  <svg className="h-10 w-10 text-gray-400 dark:text-gray-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Unable to Load Namespaces</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{error}</p>
                  <button
                    onClick={handleRetry}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {/* Namespace List */}
            {!isLoading && !isRetrying && !error && namespaces.length > 0 && (
              <div className="space-y-1 max-h-72 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                {namespaces.map((namespace) => (
                  <button
                    key={namespace.id || namespace.name}
                    onClick={() => handleSelectNamespace(namespace)}
                    className={`w-full text-left p-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-800 border-b border-gray-200 dark:border-gray-700 last:border-b-0 ${
                      getNamespaceName(namespace) === currentNamespace ? 'bg-gray-50 dark:bg-gray-800/50' : ''
                    }`}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{getNamespaceName(namespace)}</span>
                      {namespace.description && (
                        <span className="text-xs text-gray-500 dark:text-gray-500 mt-1 line-clamp-1">{namespace.description}</span>
                      )}
                    </div>
                    {getNamespaceName(namespace) === currentNamespace && (
                      <span className="text-xs bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300 px-2 py-1 rounded-full">
                        Active
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* No Namespaces */}
            {!isLoading && !isRetrying && !error && namespaces.length === 0 && (
              <div className="p-6 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-center">
                <div className="flex flex-col items-center">
                  <div className="rounded-full bg-gray-100 dark:bg-gray-700 p-3 mb-3">
                    <Building className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">No namespaces found</p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">Create a new namespace to get started</p>
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 pt-3">
            <button
              onClick={handleNavigateToCreate}
              className="flex items-center justify-center gap-2 p-3 bg-purple-600 text-white rounded-lg hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              <Plus className="w-4 h-4" />
              <span className="font-medium">Create New</span>
            </button>
            <button
              onClick={handleNavigateToManage}
              className="flex items-center justify-center gap-2 p-3 bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
            >
              <Settings className="w-4 h-4" />
              <span className="font-medium">Manage</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 